// src/components/.../BrandsStrip.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, IconButton, useMediaQuery } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { BrandsAPI } from "../../../../api/Brands";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";
import { getLangFromCookie } from "../../../../Utils/LangCookie";
import SehoLoadingSpinner from "../../../GlobalFunction/SehoLoadingSpinner";

/* ----------------------------- translations ------------------------------- */
const BRAND_TITLE_MAP = {
  ZH: "品牌資訊",
  EN: "Brands",
  JP: "ブランド情報",
};

const DEFAULT_FONTS = {
  ZH: '"LINE Sans TW","Noto Sans TC",system-ui,-apple-system,"Segoe UI",sans-serif',
  EN: '"LINE Sans TW","Noto Sans TC",system-ui,-apple-system,"Segoe UI",sans-serif',
  JP: '"LINE Sans TW","Noto Sans TC",system-ui,-apple-system,"Segoe UI",sans-serif',
};

function normKey(s) {
  return String(s || "").trim().toLowerCase();
}

/**
 * ✅ Build WP origin WITHOUT frontend port (3000)
 * Priority:
 *  1) REACT_APP_WP_ORIGIN
 *  2) REACT_APP_WP_GRAPHQL_URL (strip /graphql)
 *  3) current page protocol + hostname (NO port) + /wp-headless
 */
function getWpOriginNoPort() {
  const rawEnv =
    process.env.REACT_APP_WP_ORIGIN ||
    process.env.REACT_APP_WP_GRAPHQL_URL?.replace(/\/graphql\/?$/, "");

  if (rawEnv) return String(rawEnv).replace(/\/+$/, "");

  if (typeof window !== "undefined" && window.location) {
    const hostNoPort = `${window.location.protocol}//${window.location.hostname}`;
    return `${hostNoPort}/wp-headless`;
  }

  return "http://localhost/wp-headless";
}

/**
 * Minimal logo URL resolver (page-side only)
 * - does NOT include :3000
 * - handles common WP/legacy paths
 */
function toLogoUrl(raw) {
  if (!raw) return "";

  const s0 = String(raw).replace(/\\/g, "/").trim();
  if (!s0) return "";
  if (/^(data:|blob:)/i.test(s0)) return s0;

  // absolute URL already
  if (/^https?:\/\//i.test(s0)) return s0;

  const wpOrigin = getWpOriginNoPort();

  // legacy alias
  if (s0.startsWith("/uploads/")) {
    return `${wpOrigin}/wp-content${s0}`;
  }

  // absolute path
  if (s0.startsWith("/")) {
    return `${wpOrigin}${s0}`;
  }

  // relative fallback
  return `${wpOrigin}/wp-content/uploads/${s0}`;
}

function safeDecodeURIComponent(s) {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/* --- single tile --- */
function BrandTile({ brand, border = true }) {
  const logoUrl = toLogoUrl(brand?.brandLogo || brand?.logo || brand?.image);
  const activeLogoUrlRef = useRef(logoUrl);
  const [logoReady, setLogoReady] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  activeLogoUrlRef.current = logoUrl;

  useEffect(() => {
    setLogoReady(false);
    setLogoFailed(false);
  }, [logoUrl]);

  const handleLogoLoad = async (event) => {
    const imageElement = event.currentTarget;
    const requestedLogoUrl = activeLogoUrlRef.current;

    try {
      if (typeof imageElement.decode === "function") {
        await imageElement.decode();
      }
    } catch {
      // The logo may still be displayable even when decode rejects.
    }

    if (activeLogoUrlRef.current !== requestedLogoUrl) return;

    setLogoFailed(false);
    setLogoReady(true);
  };

  const handleLogoError = () => {
    console.warn("Brand logo failed:", brand?.brandID, logoUrl);
    setLogoFailed(true);
    setLogoReady(true);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box
        component={RouterLink}
        to="/品牌資訊"
        sx={{
          display: "grid",
          placeItems: "center",
          borderRadius: 2,
          border: border
            ? (t) =>
                `1px solid ${
                  t.palette.mode === "dark"
                    ? "rgba(255,255,255,.16)"
                    : "rgba(0,0,0,.14)"
                }`
            : "none",
          bgcolor: "background.paper",
          overflow: "hidden",
          position: "relative",
          pt: "70%",
          textDecoration: "none",
          "& img": {
            position: "absolute",
            inset: 0,
            margin: "auto",
            maxWidth: "90%",
            maxHeight: "60%",
            objectFit: "contain",
            transition:
              "opacity .2s ease, transform .35s cubic-bezier(.34,1.56,.64,1)",
          },
          "&:hover img": {
            transform: "scale(1.06)",
          },
        }}
      >
        {logoUrl && !logoReady && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 1,
              display: "grid",
              placeItems: "center",
              pointerEvents: "none",
            }}
          >
            <SehoLoadingSpinner />
          </Box>
        )}

        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt={brand?.brandName || ""}
            loading="lazy"
            decoding="async"
            data-global-loading-ignore="true"
            onLoad={handleLogoLoad}
            onError={handleLogoError}
            sx={{
              zIndex: 2,
              opacity: logoReady && !logoFailed ? 1 : 0,
            }}
          />
        ) : null}
      </Box>

      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          textAlign: "center",
          color: "#939496ff",
        }}
      >
        {brand?.brandName}
      </Typography>
    </Box>
  );
}

/* --- main section --- */
export default function BrandsStrip({
  mode,
  titleColor,
  fonts,
  border = true,
}) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const primary = getPrimaryColor(mode);

  const VISIBLE = isLgUp ? 6 : isMdUp ? 4 : 2;

  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);

  const total = brands.length;

  const [lang, setLang] = useState("ZH");

  useEffect(() => {
    const syncLang = () => {
      const c = getLangFromCookie();
      if (c === "EN" || c === "JP" || c === "ZH") setLang(c);
      else setLang("ZH");
    };

    if (typeof window !== "undefined") {
      syncLang();
      window.addEventListener("app:languageChanged", syncLang);
      return () => window.removeEventListener("app:languageChanged", syncLang);
    }
  }, []);

  const titleText = useMemo(
    () => BRAND_TITLE_MAP[lang] || BRAND_TITLE_MAP.ZH,
    [lang]
  );

  const fontFamily = useMemo(
    () => (fonts && fonts[lang]) || DEFAULT_FONTS[lang] || DEFAULT_FONTS.ZH,
    [fonts, lang]
  );

  // ✅ fix: mizuka -> mizunogi (and case-insensitive matching)
  const ALLOWED = useMemo(
    () =>
      new Set(
        ["電潔水", "擦拭君", "seho", "mizunogi", "dyson", "saraya"].map(normKey)
      ),
    []
  );

  const PRIORITY = useMemo(
    () =>
      ["電潔水", "擦拭君", "seho", "mizunogi", "dyson", "saraya"].map(normKey),
    []
  );

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      try {
        const data = await BrandsAPI.list({ lang });
        if (!alive) return;

        const arr = Array.isArray(data) ? data : [];

        const cleaned = arr
          .map((b) => {
            const rawId = String(b?.brandID ?? "").trim();
            const decodedId = safeDecodeURIComponent(rawId);
            const key =
              decodedId || rawId || String(b?.brandName ?? "").trim();

            return {
              ...b,
              __key: key,
              __keyNorm: normKey(key),
            };
          })
          .filter((b) => String(b?.brandName || "").trim() !== "-")
          .filter((b) => ALLOWED.has(b.__keyNorm));

        cleaned.sort((a, b) => {
          const aIdx = PRIORITY.indexOf(a.__keyNorm);
          const bIdx = PRIORITY.indexOf(b.__keyNorm);
          return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
        });

        setBrands(cleaned);
      } catch (err) {
        console.error("Failed to load brands:", err);
        if (!alive) return;
        setBrands([]);
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [ALLOWED, PRIORITY, lang]);

  useEffect(() => {
    setIdx(0);
  }, [VISIBLE, total]);

  // ✅ prevent arrows from trying to move when there's nothing to paginate
  const canSlide = total > VISIBLE;

  // ✅ SAFETY: never return undefined items
  const windowItems = useMemo(() => {
    if (!total) return [];

    const count = Math.min(VISIBLE, total);
    const start = ((idx % total) + total) % total;

    const out = [];
    for (let i = 0; i < count; i++) {
      const item = brands[(start + i) % total];
      if (item) out.push(item);
    }
    return out;
  }, [idx, VISIBLE, total, brands]);

  const timerRef = useRef(null);
  const clear = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const prev = () => {
    if (!canSlide) return;
    setDir(-1);
    setIdx((i) => {
      const t = brands.length;
      const step = Math.min(VISIBLE, t);
      return ((i - step) % t + t) % t;
    });
  };

  const next = () => {
    if (!canSlide) return;
    setDir(1);
    setIdx((i) => {
      const t = brands.length;
      const step = Math.min(VISIBLE, t);
      return (i + step) % t;
    });
  };

  const swipeRef = useRef({
    active: false,
    locked: false,
    lock: null,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    moved: 0,
    startedAt: 0,
  });

  const SWIPE_DISTANCE = 40;
  const SWIPE_VELOCITY = 0.25;
  const LOCK_THRESHOLD = 10;

  const onPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;

    swipeRef.current = {
      active: true,
      locked: false,
      lock: null,
      startX: e.clientX,
      startY: e.clientY,
      lastX: e.clientX,
      lastY: e.clientY,
      moved: 0,
      startedAt: performance.now(),
    };

    clear();
  };

  const onPointerMove = (e) => {
    const s = swipeRef.current;
    if (!s.active) return;

    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;

    s.lastX = e.clientX;
    s.lastY = e.clientY;
    s.moved = Math.max(s.moved, Math.abs(dx), Math.abs(dy));

    if (!s.locked && s.moved >= LOCK_THRESHOLD) {
      s.locked = true;
      s.lock = Math.abs(dx) >= Math.abs(dy) ? "x" : "y";
    }

    if (s.lock === "x") e.preventDefault?.();
  };

  const onPointerUp = () => {
    const s = swipeRef.current;
    if (!s.active) return;

    const dx = s.lastX - s.startX;
    const dy = s.lastY - s.startY;
    const dt = Math.max(1, performance.now() - s.startedAt);
    const vx = Math.abs(dx) / dt;

    swipeRef.current.active = false;

    const isHorizontal = s.lock === "x" && Math.abs(dx) > Math.abs(dy);
    if (!isHorizontal || !canSlide) return;

    if (dx <= -SWIPE_DISTANCE || vx >= SWIPE_VELOCITY) next();
    else if (dx >= SWIPE_DISTANCE || vx >= SWIPE_VELOCITY) prev();
  };

  const onPointerCancel = () => {
    swipeRef.current.active = false;
  };

  if (loading && !total) return null;
  if (!loading && total === 0) return null;

  return (
    <Box component="section" sx={{ py: { xs: 5, md: 6 } }}>
      <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 3 } }}>
        <Box sx={{ py: { xs: 0.5, md: 1.5 } }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: titleColor ?? primary,
              fontFamily: fontFamily,
            }}
          >
            {titleText}
          </Typography>
        </Box>

        <Box
          onMouseEnter={clear}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          sx={{
            position: "relative",
            overflow: "hidden",
            touchAction: "pan-y",
            userSelect: "none",
            "@keyframes slideInLeft": {
              from: { opacity: 0, transform: "translateX(-20px)" },
              to: { opacity: 1, transform: "translateX(0)" },
            },
            "@keyframes slideInRight": {
              from: { opacity: 0, transform: "translateX(20px)" },
              to: { opacity: 1, transform: "translateX(0)" },
            },
          }}
        >
          {!!total && canSlide && (
            <>
              <IconButton
                aria-label="previous"
                onClick={prev}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: 4,
                  transform: "translateY(-50%)",
                  color: "text.secondary",
                  bgcolor: "rgba(255,255,255,0.8)",
                  "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                  zIndex: 2,
                }}
              >
                <ArrowBackIosNewRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>

              <IconButton
                aria-label="next"
                onClick={next}
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: 4,
                  transform: "translateY(-50%)",
                  color: "text.secondary",
                  bgcolor: "rgba(255,255,255,0.8)",
                  "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                  zIndex: 2,
                }}
              >
                <ArrowForwardIosRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </>
          )}

          <Box
            key={`${idx}-${VISIBLE}-${total}`}
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${Math.min(VISIBLE, total)}, 1fr)`,
              gap: { xs: 2, md: 2.5 },
              animation: `${
                dir === 1 ? "slideInRight" : "slideInLeft"
              } 300ms ease`,
              transition: "transform 0.4s ease",
              WebkitUserSelect: "none",
              userSelect: "none",
            }}
          >
            {windowItems.map((b, i) => {
              const key = b?.termID || b?.brandID || b?.slug || `${idx}-${i}`;
              return <BrandTile key={key} brand={b} border={border} />;
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}